import { defineStore } from 'pinia'
import { ref } from "vue";
import { useTerritoriosStore } from './territorios'
import { useApi } from '@/composables/useApi'

export const useTrabajoStore = defineStore('trabajo', () => {
  const territorios = useTerritoriosStore()
  const api = useApi()
  const data = ref({})

  const generarId = () => Date.now().toString(36) + Math.random().toString(36).substr(2, 5)
  const crearManzanas = (total) => Array.from({ length: total }, (_, i) => i + 1)

  const templateTrabajo = (manzanasTotal) => ({
    registros: [],
    manzanasTotal,
    manzanasPendientes: crearManzanas(manzanasTotal),
    manzanasPendientesTotal: manzanasTotal,
    vecesCompletado: 0,
    enProgreso: false,
    ultimaActividad: null,
    ultimaCompletacion: null,
  })

  // Recalcula todas las estadísticas escaneando registros en orden.
  // Solo se usa en edición y eliminación; agregar actualiza incrementalmente.
  const recalcularEstadisticas = (detail) => {
    let pendientes = crearManzanas(detail.manzanasTotal)
    let vecesCompletado = 0
    let ultimaActividad = null
    let ultimaCompletacion = null

    for (const registro of detail.registros) {
      ultimaActividad = registro.fecha
      const manzanas = registro.manzanas.map(Number)
      pendientes = pendientes.filter(m => !manzanas.includes(m))

      if (pendientes.length === 0) {
        vecesCompletado++
        ultimaCompletacion = registro.fecha
        pendientes = crearManzanas(detail.manzanasTotal)
      }
    }

    detail.manzanasPendientes = pendientes
    detail.manzanasPendientesTotal = pendientes.length
    detail.vecesCompletado = vecesCompletado
    detail.ultimaActividad = ultimaActividad
    detail.ultimaCompletacion = ultimaCompletacion
    detail.enProgreso = ultimaActividad !== null && pendientes.length < detail.manzanasTotal
  }

  const resolveDetail = (uuid) => {
    if (!data.value[uuid]) {
      const territorio = territorios.byUuid.value[uuid]
      const manzanasTotal = territorio ? territorio.mzNumbers.length - 1 : 0
      data.value[uuid] = templateTrabajo(manzanasTotal)
    }
    return data.value[uuid]
  }

  // Agrega un registro y actualiza estadísticas de forma incremental.
  const agregarRegistro = (key, { fecha, conductor, manzanas, notas, turno }) => {
    const detail = resolveDetail(key)
    const manzanasNum = manzanas.map(Number)
    const id = generarId()

    detail.registros.push({ id, fecha, conductor, manzanas: manzanasNum, notas: notas || '', turno: turno || null })

    const nuevasPendientes = detail.manzanasPendientes.filter(m => !manzanasNum.includes(m))
    detail.ultimaActividad = fecha

    if (nuevasPendientes.length === 0) {
      detail.vecesCompletado++
      detail.ultimaCompletacion = fecha
      detail.manzanasPendientes = crearManzanas(detail.manzanasTotal)
      detail.manzanasPendientesTotal = detail.manzanasTotal
      detail.enProgreso = false
    } else {
      detail.manzanasPendientes = nuevasPendientes
      detail.manzanasPendientesTotal = nuevasPendientes.length
      detail.enProgreso = true
    }

    api.post('/api/work', {
      id,
      territory_id: key,
      territory_key: '',
      fecha,
      conductor: conductor || '',
      manzanas: JSON.stringify(manzanasNum),
      notas: notas || '',
      turno: turno || null,
    }).catch(e => console.error('Error al guardar registro:', e))
  }

  const editarRegistro = (key, registroId, { fecha, conductor, manzanas, notas, turno }) => {
    const detail = resolveDetail(key)
    const index = detail.registros.findIndex(r => r.id === registroId)
    if (index === -1) return
    const manzanasNum = manzanas.map(Number)
    detail.registros[index] = { id: registroId, fecha, conductor, manzanas: manzanasNum, notas: notas || '', turno: turno || null }
    recalcularEstadisticas(detail)

    api.put(`/api/work/${registroId}`, {
      territory_id: key,
      territory_key: '',
      fecha,
      conductor: conductor || '',
      manzanas: JSON.stringify(manzanasNum),
      notas: notas || '',
      turno: turno || null,
    }).catch(e => console.error('Error al editar registro:', e))
  }

  const eliminarRegistro = (key, registroId) => {
    const detail = resolveDetail(key)
    detail.registros = detail.registros.filter(r => r.id !== registroId)
    recalcularEstadisticas(detail)

    api.del(`/api/work/${registroId}`)
      .catch(e => console.error('Error al eliminar registro:', e))
  }

  // Deriva los ciclos (de inicio a completado) a partir de los registros.
  // Se usa solo para reportes, no se almacena.
  const getCiclos = (detail) => {
    if (!detail?.registros) return []
    const ciclos = []
    let pendientes = crearManzanas(detail.manzanasTotal)
    let inicioActual = null
    let conductorActual = null

    for (const registro of detail.registros) {
      if (!inicioActual) {
        inicioActual = registro.fecha
        conductorActual = registro.conductor
      }
      pendientes = pendientes.filter(m => !registro.manzanas.map(Number).includes(m))

      if (pendientes.length === 0) {
        ciclos.push({ inicio: inicioActual, fin: registro.fecha, conductor: conductorActual })
        inicioActual = null
        conductorActual = null
        pendientes = crearManzanas(detail.manzanasTotal)
      }
    }

    if (inicioActual) ciclos.push({ inicio: inicioActual, fin: null, conductor: conductorActual })
    return ciclos
  }

  const parseTimestamp = (timestamp) =>
    // Soporta tanto timestamps en ms (número o string numérico) como fechas ISO,
    // por registros antiguos guardados incorrectamente como string ISO (ver bug 31/12/1969)
    /^\d+$/.test(timestamp) ? new Date(parseInt(timestamp)) : new Date(timestamp)

  const getDate = (timestamp, err = "") => {
    if (!timestamp) return err
    const date = parseTimestamp(timestamp)
    if (isNaN(date.getTime())) return err
    return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`
  }

  const getDateTime = (timestamp, err = "") => {
    if (!timestamp) return err
    const date = parseTimestamp(timestamp)
    if (isNaN(date.getTime())) return err
    const pad = (n) => String(n).padStart(2, '0')
    return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`
  }

  const getLastRegistro = (uuid, manzana) => {
    const detail = data.value[uuid]
    if (!detail) return ''
    const registro = [...detail.registros].reverse().find(r => r.manzanas.includes(Number(manzana)))
    return registro ? getDate(registro.fecha) : ''
  }

  const iniciados = ref([])
  const todos = ref([])

  const resolveDisplayKey = (uuid) => {
    const t = territorios.byUuid.value[uuid]
    return t ? `${t.zona || ''}${t.numero}` : uuid
  }

  const getIniciados = () => {
    iniciados.value = Object.entries(data.value)
      .filter(([, item]) => item.enProgreso)
      .map(([uuid, item]) => ({
        territorioId: uuid,
        territorio: resolveDisplayKey(uuid),
        manzanasPendientesTotal: item.manzanasPendientesTotal,
        ultimoInicio: item.ultimaActividad,
      }))
      .sort((a, b) => a.ultimoInicio - b.ultimoInicio)
    return iniciados.value
  }

  const getNoTrabajados = () =>
    Object.entries(data.value)
      .filter(([, item]) => item.registros.length === 0)
      .map(([uuid, item]) => ({
        territorioId: uuid,
        territorio: resolveDisplayKey(uuid),
        manzanasPendientesTotal: item.manzanasTotal,
        ultimoFin: null,
      }))
      .sort((a, b) => b.manzanasPendientesTotal - a.manzanasPendientesTotal)

  const getCompletados = () =>
    Object.entries(data.value)
      .filter(([, item]) => item.registros.length > 0 && !item.enProgreso)
      .map(([uuid, item]) => ({
        territorioId: uuid,
        territorio: resolveDisplayKey(uuid),
        manzanasPendientesTotal: item.manzanasPendientesTotal,
        ultimoFin: item.ultimaCompletacion,
        ultimoInicio: item.ultimaActividad,
      }))
      .sort((a, b) => b.ultimoFin - a.ultimoFin)

  const setTodos = () => {
    const noTrabajados = getNoTrabajados()
    const completados = getCompletados()
    getIniciados()
    todos.value = [...noTrabajados, ...completados, ...iniciados.value]
  }

  const syncHistory = async () => {
    for (const item of territorios.list) {
      const uuid = item.uuid
      if (!uuid) continue
      const manzanasTotal = item.mzNumbers.length - 1

      if (!data.value[uuid]) {
        data.value[uuid] = templateTrabajo(manzanasTotal)
      } else if (data.value[uuid].manzanasTotal !== manzanasTotal) {
        data.value[uuid].manzanasTotal = manzanasTotal
        recalcularEstadisticas(data.value[uuid])
      }
    }
  }

  return {
    data,
    agregarRegistro,
    editarRegistro,
    eliminarRegistro,
    getCiclos,
    getDate,
    getDateTime,
    getLastRegistro,
    getIniciados,
    iniciados,
    todos,
    setTodos,
    syncHistory,
  }
}, { persist: false })
